import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { PAGE_SEARCH_ATTR } from "@/components/SearchInput/pageSearch";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { hasOpenOverlay, hasTransientPopupOpen } from "@/hooks/useOverlayDismiss";
import { shouldHandleAppChord } from "@/lib/keyboardShortcuts";

export { PAGE_SEARCH_ATTR };

/** How long to wait for a landing-page SearchInput to mount after navigation. */
const DEFAULT_FOCUS_WAIT_MS = 3000;

/**
 * Primary-nav table landings whose supporting SearchInput should receive default
 * focus when the route (or sub-tab) opens. Nested detail routes must not be added.
 */
export const TABLE_SEARCH_LANDING_PATHS = [
  "/people",
  "/facilities",
  "/events",
  "/activities/events",
  "/activities/activity-types",
  "/billing/orders",
  "/billing/payments",
  "/billing/payout-profiles",
  "/education/videos",
  "/jobs/all-jobs",
  "/jobs/my-jobs",
  "/jobs/execusearch"
] as const;

const TABLE_SEARCH_LANDING_PATH_SET: ReadonlySet<string> = new Set(TABLE_SEARCH_LANDING_PATHS);

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function isTableSearchLandingPath(pathname: unknown): boolean {
  if (typeof pathname !== "string" || pathname.length === 0) return false;
  return TABLE_SEARCH_LANDING_PATH_SET.has(normalizePathname(pathname));
}

/**
 * True when focus moved to a different interactive control during a delayed wait.
 * Retained focus on the same element (e.g. the sidebar link that triggered navigation)
 * is not treated as a user choice — default focus should still run.
 */
function hasFocusMovedFrom(baseline: Element | null): boolean {
  const active = document.activeElement;
  if (!active || active === document.body || active === document.documentElement) {
    return false;
  }
  return active !== baseline;
}

function isVisible(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    if (current.hidden) return false;
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    current = current.parentElement;
  }
  return true;
}

export function findPageSearchInput(root: ParentNode = document): HTMLInputElement | null {
  const candidates = root.querySelectorAll<HTMLInputElement>(`input[${PAGE_SEARCH_ATTR}]`);
  for (const input of candidates) {
    if (input.disabled || input.readOnly) continue;
    if (!isVisible(input)) continue;
    return input;
  }
  return null;
}

/** Focus and select the first visible page search input. Returns false when none is ready. */
export function focusPageSearchInput(): boolean {
  const input = findPageSearchInput();
  if (!input) return false;
  input.focus();
  input.select();
  return true;
}

/**
 * On primary-nav table landings, move focus to the supporting table SearchInput
 * when the pathname opens or a sibling sub-tab is selected. Query-param-only
 * changes (typing in search, filters) do not steal focus. Compact viewports
 * (mobile and tablet, under 1024px) skip default focus so the virtual keyboard
 * does not open on load. Compact vs desktop is snapshotted at route-open time so
 * a later resize or rotate does not steal focus.
 */
export function usePageSearchDefaultFocus() {
  const { pathname } = useLocation();
  const { isCompactLayout } = useIsCompactLayout();
  const isCompactLayoutRef = useRef(isCompactLayout);
  isCompactLayoutRef.current = isCompactLayout;

  useEffect(() => {
    if (isCompactLayoutRef.current) return;
    if (!isTableSearchLandingPath(pathname)) return;

    let cancelled = false;
    // Nav clicks leave focus on the sidebar link; snapshot so delayed focus can
    // still run when that same control remains focused after the search mounts.
    const focusAtEffectStart = document.activeElement;

    // Overlay/md-menu → not ready (keep waiting). Delayed path skips only when
    // focus moved to a *different* control during the wait (real user choice).
    const tryFocus = (respectUserFocus = false) => {
      if (cancelled) return true;
      if (hasOpenOverlay() || hasTransientPopupOpen()) return false;
      if (respectUserFocus && hasFocusMovedFrom(focusAtEffectStart)) return true;
      return focusPageSearchInput();
    };

    if (tryFocus()) return;

    const observer = new MutationObserver(() => {
      if (tryFocus(true)) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeoutId = window.setTimeout(() => observer.disconnect(), DEFAULT_FOCUS_WAIT_MS);

    return () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [pathname]);
}

/**
 * `/` opens MCP chat when `onOpenMcpPlayground` is provided; otherwise focuses the page
 * table SearchInput. Suppressed while overlays, menus, or go-nav mode are active.
 */
export function useSlashShortcut(onOpenMcpPlayground?: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!shouldHandleAppChord(event)) return;
      if (event.key !== "/") return;
      if (hasOpenOverlay() || hasTransientPopupOpen()) return;
      if (document.body.dataset.goNavMode === "true") return;

      let handled = false;
      if (onOpenMcpPlayground) {
        onOpenMcpPlayground();
        handled = true;
      } else {
        handled = focusPageSearchInput();
      }
      if (handled) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [onOpenMcpPlayground]);
}
